import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatTanggal } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, Bell, Megaphone, AlertCircle, BookOpen, ShoppingBag, CalendarCheck, FileText, Users, Award, Banknote, Video, MessageSquare } from "lucide-react";

const ICONS = {
  pemberitahuan: Megaphone,
  kendala: AlertCircle,
  buku_penghubung: BookOpen,
  saran_kritik: MessageSquare,
  peminjaman: ShoppingBag,
  jadwal: CalendarCheck,
  cp: FileText,
  pendampingan: Users,
  ekskul_progsus: Award,
  gaji: Banknote,
  lms: Video,
};

const ICON_COLORS = {
  pemberitahuan: "bg-indigo-50 text-indigo-600",
  kendala: "bg-rose-50 text-rose-600",
  buku_penghubung: "bg-amber-50 text-amber-600",
  saran_kritik: "bg-sky-50 text-sky-600",
  peminjaman: "bg-emerald-50 text-emerald-600",
  jadwal: "bg-violet-50 text-violet-600",
  cp: "bg-cyan-50 text-cyan-600",
  pendampingan: "bg-teal-50 text-teal-600",
  ekskul_progsus: "bg-orange-50 text-orange-600",
  gaji: "bg-lime-50 text-lime-600",
  lms: "bg-fuchsia-50 text-fuchsia-600",
};

export default function GuruNotifPopup() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (user?.role !== "guru") return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/notifications", { params: { limit: 50 } });
        if (alive) setItems((data.items || []).filter((n) => !n.read));
      } catch (e) { console.error("Load notif popup failed:", e); }
    })();
    return () => { alive = false; };
  }, [user?.id, user?.role]);

  const closeAll = async () => {
    if (closing) return;
    setClosing(true);
    try { await api.post("/notifications/read-all"); } catch (e) { console.error(e); }
    setItems([]);
    setClosing(false);
  };

  if (user?.role !== "guru" || items.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="guru-notif-popup">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden animate-fade-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Bell className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div className="leading-tight">
              <h3 className="font-bold text-slate-900">Notifikasi Baru</h3>
              <p className="text-xs text-slate-500" data-testid="guru-notif-count">{items.length} pemberitahuan belum dibaca</p>
            </div>
          </div>
          <button
            onClick={closeAll}
            disabled={closing}
            data-testid="guru-notif-popup-close"
            aria-label="Tutup notifikasi"
            className="w-9 h-9 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-800 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            const colorClass = ICON_COLORS[n.type] || "bg-slate-50 text-slate-600";
            const row = (
              <div className="flex items-start gap-3 px-5 py-3.5 border-b border-slate-100 hover:bg-indigo-50/40 transition-colors">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 leading-snug">{n.title}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{n.message}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{formatTanggal(n.created_at)}</p>
                </div>
              </div>
            );
            return n.link ? (
              <Link key={n.id} to={n.link} onClick={closeAll} data-testid={`guru-notif-item-${n.id}`}>{row}</Link>
            ) : (
              <div key={n.id} data-testid={`guru-notif-item-${n.id}`}>{row}</div>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 text-[11px] text-slate-400 shrink-0">
          Klik × di kanan atas untuk menutup & menandai semua sudah dibaca.
        </div>
      </div>
    </div>
  );
}
